import React from 'react';
import DocumentTimeline from '../../../shared/DocumentTimeline';
import useDocumentMilestoneLog from '../../../shared/useDocumentMilestoneLog';

// Every milestone type recordable against a Sales Order (see
// salesOrderController.js's own recordMilestone calls) — a type missing
// here falls back to its raw key so a future milestone never renders blank.
const MILESTONE_LABELS = {
  draft: 'Order created',
  converted: 'Converted from Quote',
  submitted: 'Submitted',
  portal_edit: 'Taken back to edit',
  notify_failed: 'New order email not sent',
  processed: 'Accepted',
  unprocessed: 'Acceptance withdrawn',
  revised: 'Revised',
  picked: 'Picked',
  packed: 'Packed',
  shipped: 'Shipped',
  on_hold: 'Placed on hold',
  off_hold: 'Hold released',
  backorder_cancelled: 'Backorder cancelled',
  shipment_reversed: 'Shipment reversed',
  payment_recorded: 'Payment recorded',
  payment_deleted: 'Payment deleted',
  payment_voided: 'Receipt voided',
  discount_added: 'Discount added',
  discount_changed: 'Discount changed',
  discount_removed: 'Discount removed',
  // Issue #269.
  invoiced: 'Invoiced',
  invoice_voided: 'Invoice voided',
};

// A Shipped row's tracking number opens the carrier's tracking page when
// the server sent one (Australia Post only), as on the customer portal.
function withTrackingLink(comment, tracking) {
  if (!comment || !tracking?.url) return comment;
  const at = comment.indexOf(tracking.number);
  if (at < 0) return comment;
  return (
    <>
      {comment.slice(0, at)}
      <a href={tracking.url} target="_blank" rel="noopener noreferrer" title="Track this shipment on Australia Post's website" style={{ color: '#1976d2' }}>
        {tracking.number}
      </a>
      {comment.slice(at + tracking.number.length)}
    </>
  );
}

// Right-hand sidebar on SalesOrderPage.js — the order's one Status History
// card: every recorded milestone, oldest first, each with any comment
// (who did it shows on hovering the date) (a Hold reason, Cancel Backorder's reason). Nothing is
// "current" here; the page header already shows the live status pill,
// including part-way states like Part Packed. A converted order records
// both Draft and Converted at the same instant, so Draft is left out when
// Converted is there. Undated rows are orders that reached a step before
// it was recorded (backfilled once, 2026-10-03). Each Ship pass is its own
// entry; its "Shipped" label opens this order's shipments
// (`shipmentsLink`) for which items went in it; Converted links to the
// source Quote.
function SalesOrderTimeline({ documentId, sourceQuote, shipmentsLink }) {
  const rows = useDocumentMilestoneLog('sales_order', documentId);
  const converted = rows.some((r) => r.milestone === 'converted');
  const steps = rows
    .filter((r) => !(converted && r.milestone === 'draft'))
    .map((r, idx) => ({
      key: `${r.milestone}-${idx}`,
      label: MILESTONE_LABELS[r.milestone] || r.milestone,
      reached: true,
      isCurrent: false,
      date: r.occurred_at,
      labelLink: ['shipped', 'shipment_reversed'].includes(r.milestone) ? shipmentsLink : null,
      link: r.milestone === 'converted' && sourceQuote ? { to: `/quotes/${sourceQuote.id}`, label: sourceQuote.display_number } : null,
      // Who did it, when recorded (issue #280), on hover of the date.
      by: r.user_name || null,
      comment: withTrackingLink(r.comment || null, r.tracking),
    }));
  return <DocumentTimeline steps={steps} title="Status History" />;
}

export default SalesOrderTimeline;
